import useDocumentTitle from '@/components/Helmet'
import LocationPicker from '@/components/GoogleMap/LocationPicker'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import axiosInstance from '@/lib/axiosInstance'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material'
import { ArrowLeft, X } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useLocation, useParams } from 'wouter'
import { z } from 'zod'
import { LocationSelector } from '@/components/Locations/Index'

const validationSchema = z.object({
    name: z.string().min(1, "Title is required").max(100, "Title cannot be more than 100 characters"),
    type: z.string().min(1, "Description is required").max(500, "Description cannot be more than 500 characters"),
    active: z.boolean(),
    geo: z.array(z.number()),
    address: z.object({
        line1: z.string().min(1, "Address Line1 is required").max(100, "Address Line1 cannot be more than 100 characters"),
        line2: z.string().min(1, "Address Line2 is required").max(100, "Address Line2 cannot be more than 100 characters"),
        city: z.string(),
        state: z.string().min(1, "State is required"),
        postalCode: z.string().min(1, "Postal code is required"),
        country: z.string().min(1, "Country is required"),
    })
    ,
    units: z.array(z.string().min(1, "Unit ID is required").max(50, "Unit ID cannot be more than 50 characters"))
})
const Add = () => {
    const { id } = useParams();
    useDocumentTitle(id ? "Property Update" : "Property Add");
    const { toast } = useToast();
    const [_, navigate] = useLocation();

    const { control, handleSubmit, formState: { errors }, setValue, getValues, setError } = useForm({
        resolver: zodResolver(validationSchema),
        defaultValues: {
            name: "",
            type: "",
            active: false,
            geo: [0, 0],
            address: { line1: "", line2: "", city: "", state: "", postalCode: "", country: "" },
            units: [""],
        },
    });
    const { fields, append, remove } = useFieldArray<any>({
        control,
        name: "units",
    });
    const [locations, setLocation] = useState("");
    const [latLang, setLatLang] = useState<[number, number]>([-6.2603, 53.3498]);
    const [images, setImages] = useState<File[]>([]);
    const [videos, setVideos] = useState<File[]>([]);
    const [imagePreview, setImagePreview] = useState<string[]>([]);
    const [videoPreview, setVideoPreview] = useState<string[]>([]);

    useEffect(() => {
        setValue("geo", [latLang[0], latLang[1]]);
    }, [latLang, setValue]);


    const fetchPropertyById = async () => {
        try {
            const response = await axiosInstance.get(`/api/properties/${id}`);
            const property = response.data;
            setValue("name", property.name);
            setValue("type", property.type);
            setValue("active", property.active);
            setValue("address", property.address);
            setValue("geo", property.geo);
            setValue("units", property.units);
            // setImagePreview(property.images)
            // setVideoPreview(property.videos)
            setLatLang([property.geo?.[0], property.geo?.[1]]);
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: error?.response?.data?.message,
            })
        }
    }

    useEffect(() => {
        if (id) {
            fetchPropertyById()
        }
    }, [id])

    const submitForm = async (data: any) => {
        try {
            const formData = new FormData();
            images.forEach((img: any) => {
                formData.append("images", img);
            });
            videos.forEach((video: any) => {
                formData.append("videos", video);
            })
            Object.keys(data).forEach((key) => {
                if (typeof data[key] === "object") {
                    formData.append(key, JSON.stringify(data[key]));
                } else {
                    formData.append(key, data[key]);
                }
            });
            if (id) {
                await axiosInstance.put(`/api/properties/${id}`, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    }
                })
                    .then((res) => {
                        if (res?.data?.success) {
                            toast({
                                variant: "success",
                                title: "Success",
                                description: "Property updated successfully",
                            })
                            navigate("/property")
                        }
                        else {
                            toast({
                                variant: "destructive",
                                title: "Error!",
                                description: res?.data?.message,
                            })
                        }
                    })
            }
            else {
                await axiosInstance.post("/api/properties", formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                })
                    .then((res) => {
                        if (res?.data?.success) {
                            toast({
                                variant: "success",
                                title: "Success",
                                description: "Property added successfully",
                            })
                            navigate("/property")
                        }
                        else {
                            toast({
                                variant: "destructive",
                                title: "Error!",
                                description: res?.data?.message,
                            })
                        }
                    })
            }
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: error?.response?.data?.message,
            })
        }
    }

    const handleDeleteImage = async (image: string) => {
        try {
            await axiosInstance.delete(`/api/properties/images/${image}/${id}`)
                .then((res) => {
                    if (res?.data?.success) {
                        toast({
                            variant: "success",
                            title: "Success",
                            description: "Image deleted successfully",
                        })
                        fetchPropertyById()
                    }
                    else {
                        toast({
                            variant: "destructive",
                            title: "Error!",
                            description: res?.data?.message,
                        })
                    }
                })
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: error?.response?.data?.message,
            })
        }
    }

    const handleDeleteVideo = async (video: string) => {
        try {
            await axiosInstance.delete(`/api/properties/videos/${video}/${id}`)
                .then((res) => {
                    if (res?.data?.success) {
                        toast({
                            variant: "success",
                            title: "Success",
                            description: "Video deleted successfully",
                        })
                        fetchPropertyById()
                    }
                    else {
                        toast({
                            variant: "destructive",
                            title: "Error!",
                            description: res?.data?.message,
                        })
                    }
                })
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: error?.response?.data?.message,
            })
        }
    }
    return (
        <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Button onClick={() => navigate("/property")}> <ArrowLeft /> Back</Button>
                <Typography sx={{ textAlign: "center", fontWeight: 600, fontSize: 24 }}>{id ? "Update" : "Add"} Property</Typography>
                <Box>

                </Box>
            </Box>
            <form onSubmit={handleSubmit(submitForm)}>
                <Grid container spacing={2} sx={{ mt: 3 }}>
                    <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                        <Controller
                            name="name"
                            control={control}
                            render={({ field }) => (
                                <>
                                    <TextField label="Property Name" fullWidth {...field} placeholder="Name" id="name" />
                                </>
                            )}
                        />
                        {
                            errors.name && (
                                <Typography sx={{ color: "red" }}>{errors.name.message}</Typography>
                            )
                        }
                    </Grid>
                    <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                        <Controller
                            name="type"
                            control={control}
                            render={({ field }) => (
                                <FormControl>
                                    <InputLabel id="demo-simple-select-label">Property Type</InputLabel>
                                    <Select value={field.value} onChange={field.onChange} fullWidth label="Property Type">
                                        <MenuItem value="" disabled>
                                            <em>Select Property Type</em>
                                        </MenuItem>
                                        <MenuItem value='residential'>Residential</MenuItem>
                                        <MenuItem value='commercial'>Commercial</MenuItem>
                                        <MenuItem value='mixed'>Mixed</MenuItem>
                                        <MenuItem value='industrial'>Industrial</MenuItem>
                                        <MenuItem value='hospitality'>Hospitality</MenuItem>
                                    </Select>
                                </FormControl>
                            )}
                        />
                        {
                            errors.type && (
                                <Typography sx={{ color: "red" }}>{errors.type.message}</Typography>
                            )
                        }

                    </Grid>
                    <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                        <Controller
                            name="address.line1"
                            control={control}
                            render={({ field }) => (
                                <>
                                    <TextField fullWidth {...field} placeholder="Address Line1" label="Address Line1" />
                                </>
                            )}
                        />
                        {
                            errors.address?.line1 && (
                                <Typography sx={{ color: "red" }}>{errors.address?.line1.message}</Typography>
                            )
                        }
                    </Grid>
                    <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                        <Controller
                            name="address.line2"
                            control={control}
                            render={({ field }) => (
                                <>
                                    <TextField fullWidth {...field} placeholder="Address Line2" label="Address Line2" />
                                </>
                            )}
                        />
                        {
                            errors.address?.line2 && (
                                <Typography sx={{ color: "red" }}>{errors.address?.line2.message}</Typography>
                            )
                        }
                    </Grid>

                    <LocationSelector control={control} errors={errors} setValue={setValue} toast={toast} setError={setError} getValues={getValues} />

                    {/* <Grid size={{ md: 12, sm: 12, xs: 12 }}>
                                <>
                                    <MultiImageUpload setImages={setImages} images={images} title="Upload Your Images" accept="image/*" support="jpg, jpeg, png" type="image" />
                                    {imagePreview.length > 0 && <Box>

                                        <Typography sx={{ fontSize: 18, fontWeight: 600, mb: 1 }}>Image Preview</Typography>
                                        <Grid container spacing={2}>
                                            {
                                                imagePreview?.map((img: any, index: number) => {
                                                    const ImageLink = API_URL + "/getImage/image/" + img
                                                    return (
                                                        <Grid size={{ md: 2, sm: 4, xs: 4 }} key={index}>
                                                            <Card sx={{ position: "relative", p: 2, textAlign: "center" }}>
                                                                <Box sx={{ position: "absolute", top: 0, right: 0, cursor: "pointer" }}>
                                                                    <Tooltip title="Remove Image">
                                                                        <X onClick={() => handleDeleteImage(img)} color="red" />
                                                                    </Tooltip>
                                                                </Box>
                                                                <CardMedia
                                                                    component="img"
                                                                    height="50"
                                                                    image={ImageLink}
                                                                    alt={`preview-${index}`}
                                                                />
                                                            </Card>
                                                        </Grid>
                                                    )
                                                })
                                            }
                                        </Grid></Box>}
                                </>
                            </Grid>

                            <Grid size={{ md: 12, sm: 12, xs: 12 }}>
                                <>
                                    <MultiImageUpload setVideos={setVideos} images={videos} title="Upload Your Videos" accept="video/*" support="mp4" type="video" />
                                    {videoPreview.length > 0 && <Box>

                                        <Typography sx={{ fontSize: 18, fontWeight: 600, mb: 1 }}>Video Preview</Typography>
                                        <Grid container spacing={2}>
                                            {
                                                videoPreview?.map((video: any, index: number) => {
                                                    const VideoLink = API_URL + "/getVideo/video/" + video
                                                    return (
                                                        <Grid size={{ md: 2, sm: 4, xs: 4 }} key={index}>
                                                            <Card sx={{ position: "relative", p: 2, textAlign: "center" }}>
                                                                <Box sx={{ position: "absolute", top: 0, right: 0, cursor: "pointer" }}>
                                                                    <Tooltip title="Remove Image">
                                                                        <X onClick={() => handleDeleteVideo(video)} color="red" />
                                                                    </Tooltip>
                                                                </Box>
                                                                <video
                                                                    controls
                                                                    width="100%"
                                                                    height="120"
                                                                    style={{ borderRadius: 8 }}
                                                                    src={VideoLink}
                                                                />
                                                            </Card>
                                                        </Grid>
                                                    )
                                                })
                                            }
                                        </Grid></Box>}
                                </>
                            </Grid> */}

                    <Grid size={{ md: 12, sm: 12, xs: 12 }} sx={{ mt: 2 }}>
                        <LocationPicker
                            setLocation={setLocation}
                            location={locations}
                            setLatLang={setLatLang}
                            latLang={latLang}
                        />
                    </Grid>

                    <Grid size={{ md: 12, sm: 12, xs: 12 }} sx={{ mt: 2 }}>
                        <Typography sx={{ fontWeight: 600, mb: 1 }}>Units (Rooms)</Typography>

                        {fields.map((field, index) => (
                            <Box key={field.id} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                <Controller
                                    name={`units.${index}`}
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            placeholder={`Room ${index + 1} Name`}
                                            label={`Unit ${index + 1}`}
                                        />
                                    )}
                                />
                                <Button
                                    type="button"
                                    className="ml-2 bg-red-600 text-white"
                                    onClick={() => remove(index)}
                                >
                                    <X size={16} />
                                </Button>
                            </Box>
                        ))}

                        <Button
                            type="button"
                            className="mt-2"
                            onClick={() => append("")}
                        >
                            + Add Unit
                        </Button>

                        {errors.units && (
                            <Typography sx={{ color: "red", mt: 1 }}>
                                {errors.units.message as string}
                            </Typography>
                        )}
                    </Grid>


                    <Grid size={{ md: 6, sm: 12, xs: 12 }} sx={{ mt: 1 }}>
                        <Controller
                            name="active"
                            control={control}
                            render={({ field }) => (
                                <div className="flex items-start space-x-2">
                                    <Checkbox
                                        id="active"
                                        checked={field.value}
                                        onCheckedChange={(checked) => field.onChange(checked)}
                                        data-testid="checkbox-active"
                                    />
                                    <Label htmlFor="active" className="text-sm text-gray-600 leading-5">
                                        Is Property Active?
                                    </Label>
                                </div>

                            )}
                        />
                        {
                            errors.active && (
                                <Typography sx={{ color: "red" }}>{errors.active.message}</Typography>
                            )
                        }
                    </Grid>


                    <Grid size={{ md: 12, sm: 12, xs: 12 }} >
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Button type="button" className='bg-red-600' onClick={() => navigate("/seller/property")}>Cancel</Button>
                            <Button type="submit">
                                {id ? "Update" : "Add"}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </form>
        </Box>
    )
}

export default Add